import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CSSProperties } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const iconDiscSizes = ['sm', 'base', 'lg', 'xl'] as const;
export type IconDiscSize = (typeof iconDiscSizes)[number];

export const iconDiscTones = ['primary', 'secondary', 'tertiary', 'quaternary', 'neutral'] as const;
export type IconDiscTone = (typeof iconDiscTones)[number];

interface SizeSpec {
  box: string;
  icon: number;
}

const sizeSpecs: Record<IconDiscSize, SizeSpec> = {
  /** 20px — inline tutorial-style icon chip. */
  sm: { box: 'w-5 h-5', icon: 10 },
  /** 40px — sub-section icon next to a step header. */
  base: { box: 'w-10 h-10', icon: 18 },
  /** 48px — modal-centerpiece feature icon. */
  lg: { box: 'w-12 h-12', icon: 20 },
  /** 64px — welcome / empty-state hero icon. */
  xl: { box: 'w-16 h-16', icon: 30 },
};

const toneClasses: Record<IconDiscTone, string> = {
  primary: 'bg-primary-subtle text-primary-emphasized',
  secondary: 'bg-secondary-subtle text-secondary-emphasized',
  tertiary: 'bg-tertiary-subtle text-tertiary-emphasized',
  quaternary: 'bg-quaternary-subtle text-quaternary-emphasized',
  neutral: 'bg-content-focal border border-content-border text-content-deemphasized',
};

export interface IconDiscProps {
  icon: IconDefinition;
  size?: IconDiscSize;
  tone?: IconDiscTone;
  /** Style applied to the SVG itself. Useful for inline-text usage where
   * the icon needs an optical vertical-align tweak. */
  iconStyle?: CSSProperties;
  /** Style applied to the wrapper span. Useful for inline-text usage where
   * the whole disc needs a vertical-align baseline anchor. */
  style?: CSSProperties;
  className?: ClassName;
}

/**
 * Circular icon container used as a "feature icon" affordance — the
 * subtle-tinted disc that draws attention to a modal centerpiece, a
 * section header, or a tutorial inline reference.
 *
 * Four sizes cover all current usages: inline (sm), section (base),
 * modal (lg), hero (xl).
 */
export const IconDisc = ({
  icon,
  size = 'lg',
  tone = 'primary',
  iconStyle,
  style,
  className,
}: IconDiscProps) => {
  const spec = sizeSpecs[size];
  return (
    <span
      className={twx(
        'inline-flex items-center justify-center rounded-full shrink-0',
        spec.box,
        toneClasses[tone],
        className,
      )}
      style={style}
    >
      <FontAwesomeIcon icon={icon} style={{ width: spec.icon, height: spec.icon, ...iconStyle }} />
    </span>
  );
};
